import { useCallback, useLayoutEffect, useRef, useState } from 'react';

interface StoryLayout {
  isPinned: boolean;
  stickyTop: number;
  stageHeight: number;
  trackHeight: number;
}

const initialLayout: StoryLayout = {
  isPinned: false,
  stickyTop: 120,
  stageHeight: 600,
  trackHeight: 600,
};

/**
 * Native-scroll story: one bounded sticky stage and a short measured scroll track.
 * No wheel/touch interception and no collection of empty viewport-sized text rows.
 * Small or short viewports use the ordinary stacked content instead.
 */
export function useActiveStep(count: number) {
  const [active, setActive] = useState(0);
  const [layout, setLayout] = useState<StoryLayout>(initialLayout);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const desktop = window.matchMedia('(min-width: 1024px) and (min-height: 640px)');
    let frame = 0;

    const updateActive = () => {
      frame = 0;
      const track = trackRef.current;
      const stage = stageRef.current;
      if (!desktop.matches || !track || !stage) return;
      const top = Number.parseFloat(window.getComputedStyle(stage).top) || 120;
      const travel = Math.max(1, track.offsetHeight - stage.offsetHeight);
      // Reading the current rect also handles images/fonts above the section settling.
      const progress = Math.max(0, Math.min(1, (top - track.getBoundingClientRect().top) / travel));
      const next = Math.min(Math.max(0, count - 1), Math.floor(progress * Math.max(1, count)));
      setActive((previous) => (previous === next ? previous : next));
    };

    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(updateActive);
    };

    const measure = () => {
      const headerValue = window.getComputedStyle(document.documentElement).getPropertyValue('--site-header-height');
      const stickyTop = (Number.parseFloat(headerValue) || 104) + 16;
      const stageHeight = Math.max(0, Math.min(680, window.innerHeight - stickyTop - 24));
      const stepDistance = Math.max(140, Math.min(220, window.innerHeight * 0.2));
      const next: StoryLayout = {
        isPinned: desktop.matches,
        stickyTop,
        stageHeight,
        // Four modest scroll intervals keep every active step readable before release.
        trackHeight: stageHeight + Math.max(1, count) * stepDistance,
      };
      setLayout((previous) =>
        previous.isPinned === next.isPinned && previous.stickyTop === next.stickyTop &&
        previous.stageHeight === next.stageHeight && previous.trackHeight === next.trackHeight
          ? previous
          : next
      );
      schedule();
    };

    measure();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', measure, { passive: true });
    desktop.addEventListener('change', measure);
    document.fonts?.ready.then(schedule);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', measure);
      desktop.removeEventListener('change', measure);
    };
  }, [count]);

  const goToStep = useCallback((index: number) => {
    const track = trackRef.current;
    const stage = stageRef.current;
    if (!track || !stage) return;
    const top = Number.parseFloat(window.getComputedStyle(stage).top) || 120;
    const travel = Math.max(0, track.offsetHeight - stage.offsetHeight);
    const selected = Math.max(0, Math.min(count - 1, index));
    const target = window.scrollY + track.getBoundingClientRect().top - top + travel * ((selected + 0.5) / Math.max(1, count));
    window.scrollTo({
      top: Math.max(0, target),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }, [count]);

  return { active, trackRef, stageRef, goToStep, ...layout };
}

